import { StoredProfile } from '@/utils/storage'
import {
  calculatePersonalDayNumber,
  calculateUniversalDayNumber,
  calculateUniversalWeekNumber,
  detectMasterNumber,
  getNumerologyMeaning,
} from '@/utils/numerology'
import { texts } from '@/texts'
import { fmt, md } from '@/utils/fmt'
import { composeNumeroscope, daySerial, pickVariant, type NumeroscopeResult } from '@/numeroscope'
import zahlioImg from '@/assets/zahlio.png'

interface DailyHoroscopeProps {
  profile: StoredProfile
}

// Kleine Funkelchen direkt auf dem Goldring der Kugel (Ringzentrum ~68%/49,3%,
// Radius ~14%) – leicht unregelmäßige Winkel/Radien, wo schon statische Lichter sind.
const ZAHLIO_SPARKLES = [
  { left: '66.5%', top: '35.4%', size: '3px', delay: '0.4s', dur: '2.6s' },
  { left: '77%', top: '38.5%', size: '4px', delay: '1.7s', dur: '3.1s' },
  { left: '82%', top: '46%', size: '3px', delay: '0.9s', dur: '2.4s' },
  { left: '77%', top: '60%', size: '3px', delay: '2.3s', dur: '3.4s' },
  { left: '57%', top: '57%', size: '2px', delay: '1.2s', dur: '2.8s' },
  { left: '57%', top: '41%', size: '3px', delay: '3.0s', dur: '3.7s' },
] as const

// Feiner Goldstaub, locker in alle Richtungen um Zahlio verteilt (hinter der Figur).
const ZAHLIO_DUST = [
  { left: '24%', top: '29%', size: '3px', delay: '0.0s', dur: '6.5s' },
  { left: '50%', top: '13%', size: '2px', delay: '2.4s', dur: '7.2s' },
  { left: '79%', top: '23%', size: '3px', delay: '1.1s', dur: '5.8s' },
  { left: '89%', top: '47%', size: '2px', delay: '3.6s', dur: '6.9s' },
  { left: '73%', top: '80%', size: '3px', delay: '0.7s', dur: '6.2s' },
  { left: '28%', top: '83%', size: '2px', delay: '2.9s', dur: '7.6s' },
  { left: '11%', top: '56%', size: '3px', delay: '1.8s', dur: '6.0s' },
] as const

interface MoonPhaseInfo {
  name: string
  emoji: string
  energy: string
}

function getMoonPhaseInfo(date: Date, serial: number): MoonPhaseInfo {
  const synodicMonth = 29.53058867
  const knownNewMoon = Date.UTC(2000, 0, 6, 18, 14, 0)
  const daysSinceKnownNewMoon = (date.getTime() - knownNewMoon) / 86400000
  const moonAge = ((daysSinceKnownNewMoon % synodicMonth) + synodicMonth) % synodicMonth
  const phaseIndex = Math.floor((moonAge / synodicMonth) * 8) % 8

  const m = texts.moonPhases
  const phases = [
    m.newMoon,
    m.waxingCrescent,
    m.firstQuarter,
    m.waxingGibbous,
    m.fullMoon,
    m.waningGibbous,
    m.lastQuarter,
    m.waningCrescent,
  ]
  const raw = phases[phaseIndex]

  // Mondbotschaft kann 1..n Varianten haben — eine pro Tag (eigener Salt-Offset).
  return { name: raw.name, emoji: raw.emoji, energy: pickVariant(raw.energy, serial, 404) }
}

function SectionCard({ children, accent }: { children: React.ReactNode; accent?: boolean }) {
  return <div className={`mg-card${accent ? ' accent' : ''}`}>{children}</div>
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="mg-card-title">{children}</h3>
}

function BodyText({ children, secondary }: { children: React.ReactNode; secondary?: boolean }) {
  return (
    <p className={`mg-body${secondary ? ' sec' : ''}`} style={{ marginBottom: 'var(--spacing-2)' }}>
      {children}
    </p>
  )
}

function NumberBadge({ n, label }: { n: number; label: string }) {
  return (
    <div className="mg-num-badge">
      <span className="n">{n}</span>
      <span className="l">{label}</span>
    </div>
  )
}

function NumeroscopeView({ ns, moonPhase }: {
  ns: NumeroscopeResult
  moonPhase: MoonPhaseInfo
}) {
  const meaning = getNumerologyMeaning(ns.personalDayNumber)

  return (
    <>
      {/* Hero: Zahlio hält die persönliche Tageszahl in seiner Kugel */}
      <div className="mg-hero mg-hero-zahlio">
        <div className="mg-zahlio-wrap">
          {/* Goldstaub-Aura hinter Zahlio */}
          <span className="mg-zahlio-aura" />
          {ZAHLIO_DUST.map((d, i) => (
            <span
              key={`dust-${i}`}
              className="mg-dust"
              style={{
                left: d.left,
                top: d.top,
                width: d.size,
                height: d.size,
                animationDelay: d.delay,
                animationDuration: d.dur,
              }}
            />
          ))}
          <img className="mg-zahlio-img" src={zahlioImg} alt="Zahlio" draggable={false} />
          <span className="mg-zahlio-glow" />
          <div className="mg-zahlio-number">{ns.personalDayNumber}</div>
          {/* Animierte Funkelchen rund um die Kugel */}
          {ZAHLIO_SPARKLES.map((s, i) => (
            <span
              key={i}
              className="mg-sparkle"
              style={{
                left: s.left,
                top: s.top,
                width: s.size,
                height: s.size,
                animationDelay: s.delay,
                animationDuration: s.dur,
              }}
            />
          ))}
        </div>
        <p className="mg-hero-label">{texts.dailyHoroscope.personalDayLabel}</p>
      </div>

      {/* Dimension 3: Persönlicher Auftrag (Herzstück) */}
      <SectionCard accent>
        <SectionHeading>🔮 Deine persönliche Tagesenergie</SectionHeading>
        <BodyText>{md(ns.personalDayText)}</BodyText>
      </SectionCard>

      {/* Dimension 4: Meisterzahl-Upgrade */}
      {ns.masterAdditive && (
        <SectionCard>
          <SectionHeading>⭐ {ns.masterAdditive.title}</SectionHeading>
          <BodyText>{md(ns.masterAdditive.text)}</BodyText>
        </SectionCard>
      )}

      {/* Dimension 2: Kollektive Tagesenergie */}
      <SectionCard>
        <NumberBadge n={ns.collectiveDayNumber} label="Kollektive Tageszahl" />
        <BodyText>{md(ns.collectiveDayText)}</BodyText>
      </SectionCard>

      {/* Dimension 1: Wochenenergie */}
      <SectionCard>
        <SectionHeading>🌐 Die {ns.weekNumber}er-Woche</SectionHeading>
        <BodyText>{md(ns.weekText)}</BodyText>
      </SectionCard>

      {/* Mondphase */}
      <SectionCard>
        <SectionHeading>{moonPhase.emoji} {texts.dailyHoroscope.moonPhaseTitle}</SectionHeading>
        <BodyText>
          {fmt(texts.dailyHoroscope.moonPhaseSentence, { phaseName: moonPhase.name })}
        </BodyText>
        <BodyText secondary>{moonPhase.energy}</BodyText>
      </SectionCard>

      {/* Charakteristiken */}
      <SectionCard>
        <SectionHeading>{texts.dailyHoroscope.characteristicsTitle}</SectionHeading>
        <div className="mg-chips">
          {meaning.characteristics.map((char) => (
            <span key={char} className="mg-chip">{char}</span>
          ))}
        </div>
      </SectionCard>
    </>
  )
}

export function DailyHoroscope({ profile }: DailyHoroscopeProps) {
  const today = new Date()
  const dob = new Date(profile.dateOfBirth)
  const birthMonth = dob.getMonth() + 1
  const birthDay = dob.getDate()

  const personalDayNumber = calculatePersonalDayNumber(
    birthMonth, birthDay,
    today.getFullYear(), today.getMonth() + 1, today.getDate(),
  )
  const universalDayNumber = calculateUniversalDayNumber(
    today.getFullYear(), today.getMonth() + 1, today.getDate(),
  )
  const universalWeekNumber = calculateUniversalWeekNumber(today)
  const masterNumber = detectMasterNumber(profile.dateOfBirth)
  const serial = daySerial(today.getFullYear(), today.getMonth() + 1, today.getDate())
  const moonPhase = getMoonPhaseInfo(today, serial)

  const ns = composeNumeroscope(
    profile.name,
    profile.lifePathNumber,
    universalWeekNumber,
    universalDayNumber,
    personalDayNumber,
    masterNumber,
    serial,
  )

  const formatDate = (date: Date) =>
    new Intl.DateTimeFormat('de-DE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date)

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--spacing-4)',
    }}>
      {/* Datum */}
      <div className="mg-date-head">
        <p className="t">{texts.dailyHoroscope.todayLabel}</p>
        <p className="d">{formatDate(today)}</p>
      </div>

      <NumeroscopeView ns={ns} moonPhase={moonPhase} />
    </div>
  )
}
